import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useQuery } from '../hooks/useQuery';
import { useConfig, jiraIssueUrl } from '../hooks/useConfig';
import { useUrlParams } from '../hooks/useUrlState';
import { api, downloadFile, EngagementBudgetRow, Invoice } from '../lib/api';
import { fmtCurrency, fmtCurrencyCents } from '../lib/fmt';
import {
  SectionLabel, Card, CardHeader, Table, Th, Tr, Td, Badge,
  ActionButton, Spinner, EmptyState, MetricCard, SortableTh, Modal, UpdatingBadge,
} from '../components/ui';
import { InvoiceLink } from '../components/InvoiceLink';
import { EngagementContractPanel } from '../components/EngagementContractPanel';
import { useTableSort } from '../hooks/useTableSort';

type ModelTab =
  'all' | 'fixed' | 'tm' | 'overrun' | 'untracked' | 'overdue' | 'no_invoices'
  | 'breach' | 'over_high' | 'over_median' | 'no_contract';
type OverdueBucket = 'any' | '31' | '61' | '91';

const TABS: Array<{ id: ModelTab; label: string; help: string }> = [
  { id: 'all',         label: 'All',           help: 'Every engagement in your scope' },
  // Contract-compliance buckets first — this report exists so finance can hold
  // billing to the signed terms, and a shall-not-exceed breach is the single
  // most urgent thing on the page.
  { id: 'breach',      label: 'Past SNE',      help: 'Burn has passed a shall-not-exceed ceiling, or a fixed fee. A breach of terms, not a warning.' },
  { id: 'over_high',   label: 'Past high',     help: 'Burn has passed the high estimate but is still inside the shall-not-exceed ceiling' },
  { id: 'over_median', label: 'Past median',   help: 'Burn has passed the median estimate — inside the band, worth watching' },
  { id: 'fixed',       label: 'Fixed fee',     help: 'Sold for a single price' },
  { id: 'tm',          label: 'T&M',           help: 'Hourly contract with a not-to-exceed budget' },
  { id: 'overrun',     label: 'Over budget',   help: 'Burned value exceeds the tracked budget ceiling' },
  { id: 'no_contract', label: 'No terms',      help: 'No contract terms captured — running on the Jira budget field alone, with no band to check against' },
  { id: 'untracked',   label: 'No budget',     help: 'No budget value at all — burn is unevaluated' },
  { id: 'overdue',     label: 'Overdue',       help: 'Engagements with at least one overdue invoice' },
  { id: 'no_invoices', label: 'No invoices',   help: 'Engagements with no ChargeOver invoice linked' },
];

/** Badge for the tightest contractual line burn has crossed. Null contract →
 *  no badge at all: absence of terms is not the same as being within them. */
function ThresholdBadge({ row }: { row: EngagementBudgetRow }) {
  const t = row.thresholds;
  if (!t) return <span style={{ color: 'var(--color-text-hint)', fontSize: 11 }}>—</span>;
  switch (t.worst) {
    case 'not_to_exceed': return <Badge variant="red">Past SNE</Badge>;
    case 'fixed_fee':     return <Badge variant="red">Over fee</Badge>;
    case 'high':          return <Badge variant="amber">Past high</Badge>;
    case 'median':        return <Badge variant="amber">Past median</Badge>;
    case 'low':           return <Badge variant="blue">Past low</Badge>;
    default:              return <Badge variant="green">Within band</Badge>;
  }
}

/** The Terms cell is the way in to contract maintenance, so it has to look like
 *  a control on every row — including the rows with nothing captured yet, where
 *  the threshold badge is only an em-dash. Rendered in the same blue the Jira
 *  column uses, which is this table's established "this is clickable" cue. */
function TermsCell({ row, onOpen }: { row: EngagementBudgetRow; onOpen: () => void }) {
  const has = row.contract_id != null;
  return (
    <button
      // Stops propagation so it doesn't also fire the row's invoice modal.
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
      title={has ? 'View or edit contract terms and change orders' : 'No terms captured — add them'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        background: 'none', border: 'none', padding: 0,
        font: 'inherit', cursor: 'pointer', textAlign: 'left',
      }}
    >
      <ThresholdBadge row={row} />
      {row.change_order_count > 0 && (
        <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
          {row.change_order_count} CO{row.change_order_count === 1 ? '' : 's'}
          {row.pending_change_order_count > 0 && ` (${row.pending_change_order_count} upcoming)`}
        </span>
      )}
      <span style={{ fontSize: 11, color: 'var(--color-blue)' }}>
        {has ? 'Terms' : 'Add terms'}
      </span>
    </button>
  );
}

const DEFAULTS = { tab: 'all', search: '', overdue: 'any' };

export default function AdminEngagementBudgetPage() {
  const { data, isLoading, isFetching, error, refetch } = useQuery(() => api.admin.engagementBudget());
  const [params, setParams] = useUrlParams(DEFAULTS);
  const tab = params.tab as ModelTab;
  const search = params.search;
  const overdueBucket = (params.overdue as OverdueBucket) || 'any';

  // Apply overdue-bucket filter only when the Overdue tab is active. When
  // another tab is selected, overdue filter is irrelevant.
  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    return data.engagements.filter((e) => {
      if (tab === 'fixed'       && !e.is_fixed_fee)             return false;
      if (tab === 'tm'          && e.is_fixed_fee)              return false;
      if (tab === 'overrun'     && !e.is_overrun)               return false;
      if (tab === 'untracked'   && e.contract_value != null)    return false;
      if (tab === 'no_invoices' && (e.billing?.invoice_count ?? 0) > 0) return false;
      // Contract-compliance buckets. "Past high" and "past median" exclude the
      // more serious levels so each tab is the set that needs THAT response —
      // an engagement in breach shouldn't also sit in the watch-list tab.
      if (tab === 'breach'
        && !(e.thresholds?.over_not_to_exceed || e.thresholds?.over_fixed_fee)) return false;
      if (tab === 'over_high'
        && !(e.thresholds?.over_high && !e.thresholds.over_not_to_exceed)) return false;
      if (tab === 'over_median'
        && !(e.thresholds?.over_median && !e.thresholds.over_high)) return false;
      if (tab === 'no_contract' && e.contract_id != null)       return false;
      if (tab === 'overdue') {
        const b = e.billing;
        if (!b) return false;
        if (overdueBucket === 'any' && b.overdue_balance <= 0) return false;
        // Cumulative filters — "31+" means at least one invoice ≥ 31 days
        // overdue, which is the sum of the 31-60, 61-90, and 91+ buckets.
        if (overdueBucket === '31'
          && (b.overdue_31_60_count + b.overdue_61_90_count + b.overdue_91_plus_count) === 0) return false;
        if (overdueBucket === '61'
          && (b.overdue_61_90_count + b.overdue_91_plus_count) === 0) return false;
        if (overdueBucket === '91' && b.overdue_91_plus_count === 0) return false;
      }
      if (!needle) return true;
      return e.jira_issue_key.toLowerCase().includes(needle)
        || (e.summary ?? '').toLowerCase().includes(needle)
        || (e.tempo_account_name ?? '').toLowerCase().includes(needle);
    });
  }, [data, tab, overdueBucket, search]);

  const counts = useMemo(() => {
    const empty = {
      all: 0, fixed: 0, tm: 0, overrun: 0, untracked: 0, overdue: 0, no_invoices: 0,
      breach: 0, over_high: 0, over_median: 0, no_contract: 0,
    };
    if (!data) return empty;
    const overdueEng = data.engagements.filter((e) => (e.billing?.overdue_balance ?? 0) > 0).length;
    const noInv = data.engagements.filter((e) => (e.billing?.invoice_count ?? 0) === 0).length;
    // Counted the same way the tab filters slice, so the badge always matches
    // the row count the operator lands on.
    const t = data.engagements;
    return {
      all: t.length,
      fixed: data.totals.fixed_fee_count,
      tm: t.length - data.totals.fixed_fee_count,
      overrun: data.totals.overrun_count,
      untracked: data.totals.no_budget_count,
      overdue: overdueEng,
      no_invoices: noInv,
      breach: t.filter((e) => e.thresholds?.over_not_to_exceed || e.thresholds?.over_fixed_fee).length,
      over_high: t.filter((e) => e.thresholds?.over_high && !e.thresholds.over_not_to_exceed).length,
      over_median: t.filter((e) => e.thresholds?.over_median && !e.thresholds.over_high).length,
      no_contract: t.filter((e) => e.contract_id == null).length,
    };
  }, [data]);

  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={error} />;
  if (!data) return null;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <SectionLabel>Engagement budget vs. actual</SectionLabel>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          {isFetching && <UpdatingBadge />}
          <ActionButton onClick={() => setParams(DEFAULTS)} variant="default">Clear filters</ActionButton>
          <ActionButton
            onClick={() => downloadFile('/admin/reports/engagement-budget?format=csv', 'engagement-budget.csv')}
            variant="default"
          >
            Export CSV
          </ActionButton>
          <ActionButton onClick={refetch} variant="default">Refresh</ActionButton>
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10 }}>
        <MetricCard
          label="Engagements"
          value={data.totals.engagement_count}
          sub={`${data.totals.fixed_fee_count} fixed fee · ${data.totals.engagement_count - data.totals.fixed_fee_count} T&M`}
        />
        <MetricCard
          label="Total budget"
          value={fmtCurrency(data.totals.total_contract)}
          sub={data.totals.no_budget_count > 0 ? `${data.totals.no_budget_count} engagements missing a budget` : 'All engagements have a budget'}
          accent={data.totals.no_budget_count > 0 ? 'amber' : undefined}
        />
        <MetricCard
          label="Outstanding AR"
          value={fmtCurrencyCents(data.totals.total_outstanding)}
          sub={data.totals.total_overdue > 0 ? `${fmtCurrencyCents(data.totals.total_overdue)} overdue` : 'Nothing overdue'}
          accent={data.totals.total_overdue > 0 ? 'amber' : 'green'}
        />
        <MetricCard
          label="Past contract limit"
          value={data.totals.over_sne_count + data.totals.over_fixed_fee_count}
          accent={(data.totals.over_sne_count + data.totals.over_fixed_fee_count) > 0 ? 'amber' : 'green'}
          sub={
            (data.totals.over_sne_count + data.totals.over_fixed_fee_count) > 0
              ? 'Shall-not-exceed or fixed fee passed'
              : `${data.totals.over_high_count} past high · ${data.totals.contracted_count} with terms`
          }
          onClick={() => setParams({ tab: 'breach' })}
        />
      </div>

      <Card>
        <CardHeader>
          {TABS.map((t) => (
            <TabButton
              key={t.id}
              active={tab === t.id}
              onClick={() => setParams({ tab: t.id })}
              help={t.help}
            >
              {t.label} <Badge variant={
                counts[t.id] === 0 ? 'green'
                  : t.id === 'breach' ? 'red'
                  : (t.id === 'overrun' || t.id === 'over_high' || t.id === 'over_median') ? 'amber'
                  : 'gray'
              }>{counts[t.id]}</Badge>
            </TabButton>
          ))}
          <span style={{ marginLeft: 'auto' }}>
            <input
              type="search"
              placeholder="Filter by key, summary, account…"
              value={search}
              onChange={(e) => setParams({ search: e.target.value })}
              style={{
                fontSize: 11, padding: '4px 8px', width: 260,
                border: '0.5px solid var(--color-border-strong)',
                borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)',
              }}
            />
          </span>
        </CardHeader>

        {/* Sub-chips for the Overdue tab: narrow to invoices past N days. */}
        {tab === 'overdue' && (
          <div style={{
            display: 'flex', gap: 6, padding: '8px 16px',
            borderBottom: '0.5px solid var(--color-border)',
            background: 'var(--color-bg)',
          }}>
            {([
              { id: 'any' as OverdueBucket, label: 'All' },
              { id: '31'  as OverdueBucket, label: '31+ days' },
              { id: '61'  as OverdueBucket, label: '61+ days' },
              { id: '91'  as OverdueBucket, label: '91+ days' },
            ]).map((b) => (
              <button
                key={b.id}
                onClick={() => setParams({ overdue: b.id })}
                style={{
                  fontSize: 11, padding: '4px 10px',
                  borderRadius: 4,
                  border: overdueBucket === b.id ? '0.5px solid var(--color-red)' : '0.5px solid var(--color-border-strong)',
                  background: overdueBucket === b.id ? 'var(--color-red-bg)' : 'var(--color-surface)',
                  color: overdueBucket === b.id ? 'var(--color-red-text)' : 'var(--color-text-muted)',
                  cursor: 'pointer',
                }}
              >
                {b.label}
              </button>
            ))}
          </div>
        )}

        <BudgetTable rows={filtered} searchActive={!!search.trim()} />
      </Card>
    </>
  );
}

function TabButton({ active, onClick, children, help }: { active: boolean; onClick: () => void; children: React.ReactNode; help?: string }) {
  return (
    <button
      onClick={onClick}
      title={help}
      style={{
        background: 'none',
        border: 'none',
        borderBottom: active ? '2px solid var(--color-blue)' : '2px solid transparent',
        padding: '8px 16px',
        fontSize: 12,
        fontWeight: 500,
        color: active ? 'var(--color-text)' : 'var(--color-text-muted)',
        cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}
    >
      {children}
    </button>
  );
}

/** Rank for sorting the threshold column by severity rather than alphabetically,
 *  so the rows that need action sort to one end. -1 keeps engagements with no
 *  captured terms out of the severity ordering entirely. */
const THRESHOLD_RANK: Record<string, number> = {
  none: 0, low: 1, median: 2, high: 3, fixed_fee: 4, not_to_exceed: 5,
};

function BudgetTable({ rows, searchActive }: { rows: EngagementBudgetRow[]; searchActive: boolean }) {
  const { config } = useConfig();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canOpenWorkspace = user?.permissions?.includes('report_engagement_workspace') === true;
  const [billingFor, setBillingFor] = useState<EngagementBudgetRow | null>(null);
  const [contractFor, setContractFor] = useState<EngagementBudgetRow | null>(null);
  const { sorted, sort } = useTableSort(
    rows,
    {
      engagement:   (r) => r.summary,
      account:      (r) => r.tempo_account_name ?? '',
      model:        (r) => r.is_fixed_fee ? 'Fixed fee' : 'T&M',
      terms:        (r) => r.thresholds ? THRESHOLD_RANK[r.thresholds.worst] ?? 0 : -1,
      budget:       (r) => r.contract_value ?? -1,
      burned:       (r) => r.burned_value,
      remaining:    (r) => r.remaining ?? -Infinity,
      pct_consumed: (r) => r.pct_consumed ?? -1,
      billed:       (r) => r.billing?.billed_total ?? 0,
      fees:         (r) => r.billing?.fees_total ?? 0,
      paid:         (r) => r.billing?.paid_total ?? 0,
      balance:      (r) => r.billing?.outstanding_balance ?? 0,
      overdue:      (r) => r.billing?.overdue_balance ?? 0,
      writeoff:     (r) => r.billing?.writeoff_total ?? 0,
      credit:       (r) => r.billing?.credit_total ?? 0,
      deposit:      (r) => r.billing?.deposit_applied_total ?? 0,
      deliverables: (r) => r.deliverable_count,
      status:       (r) => r.status,
    },
    { key: 'engagement', dir: 'asc' },
  );

  if (rows.length === 0) {
    return <EmptyState message={searchActive ? 'No matches.' : 'No engagements in this bucket.'} />;
  }

  return (
    <>
      <div style={{ maxHeight: 'calc(100vh - 360px)', overflow: 'auto' }}>
        <Table>
          <thead>
            <tr>
              <SortableTh columnKey="engagement"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Engagement</SortableTh>
              <SortableTh columnKey="account"      sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Account</SortableTh>
              <SortableTh columnKey="model"        sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Model</SortableTh>
              <SortableTh columnKey="terms"        sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Terms</SortableTh>
              <SortableTh columnKey="budget"       sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Budget</SortableTh>
              <SortableTh columnKey="burned"       sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Burned</SortableTh>
              {/* "Billed" has always been services-only; the fee column makes
                  that visible instead of leaving it as a footnote in the API. */}
              <SortableTh columnKey="billed"       sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Billed (services)</SortableTh>
              <SortableTh columnKey="fees"         sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Fees</SortableTh>
              <SortableTh columnKey="paid"         sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Paid</SortableTh>
              <SortableTh columnKey="balance"      sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Outstanding</SortableTh>
              <SortableTh columnKey="overdue"      sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Overdue</SortableTh>
              <SortableTh columnKey="writeoff"     sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Write-off</SortableTh>
              <SortableTh columnKey="credit"       sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Credit</SortableTh>
              <SortableTh columnKey="deposit"      sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Deposit used</SortableTh>
              <SortableTh columnKey="deliverables" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Deliverables</SortableTh>
              <SortableTh columnKey="status"       sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Status</SortableTh>
              <Th>Jira</Th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const url = jiraIssueUrl(config, r.jira_issue_key);
              const b = r.billing;
              return (
                <Tr key={r.jira_issue_id} onClick={b && b.invoice_count > 0 ? () => setBillingFor(r) : undefined}>
                  <Td bold>
                    {/* The engagement key is the way into the workspace. Only
                        rendered as a link for people who hold the key, so it
                        can't lead to a 403. */}
                    {canOpenWorkspace ? (
                      <a
                        href={`/admin/engagements/${encodeURIComponent(r.jira_issue_id)}/w/overview`}
                        onClick={(ev) => {
                          if (ev.metaKey || ev.ctrlKey || ev.shiftKey) return;   // let new-tab through
                          ev.preventDefault();
                          ev.stopPropagation();
                          navigate(`/admin/engagements/${encodeURIComponent(r.jira_issue_id)}/w/overview`);
                        }}
                        style={{ color: 'var(--color-blue)', marginRight: 6 }}
                        title="Open the engagement workspace"
                      >
                        {r.jira_issue_key}
                      </a>
                    ) : (
                      <span style={{ marginRight: 6 }}>{r.jira_issue_key}</span>
                    )}
                    <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>{r.summary}</span>
                  </Td>
                  <Td muted style={{ fontSize: 11 }}>{r.tempo_account_name ?? '—'}</Td>
                  <Td>{r.is_fixed_fee ? <Badge variant="blue">Fixed fee</Badge> : <Badge variant="gray">T&amp;M</Badge>}</Td>
                  <Td><TermsCell row={r} onOpen={() => setContractFor(r)} /></Td>
                  <Td right>{r.contract_value != null ? fmtCurrency(r.contract_value) : '—'}</Td>
                  <Td right>{fmtCurrency(r.burned_value)}</Td>
                  <Td right>
                    {b ? fmtCurrencyCents(b.billed_total) : '—'}
                    {/* Any fee on an unsplit invoice is still inside the figure
                        above, so say so rather than letting it read as clean. */}
                    {b && b.unsplit_billed_total > 0 && (
                      <span
                        style={{ display: 'block', fontSize: 10, color: 'var(--color-amber-text)' }}
                        title={`${fmtCurrencyCents(b.unsplit_billed_total)} of this is on invoices ChargeOver hasn't returned line items for, so any fees on them are still counted inside it. Run the invoice line-item backfill on Admin → Data sync.`}
                      >
                        {fmtCurrencyCents(b.unsplit_billed_total)} unsplit
                      </span>
                    )}
                  </Td>
                  <Td right muted>
                    <span title="CC processing / convenience fees. Passes through to the payment processor — not money available to deliver with.">
                      {b && b.fees_total > 0 ? fmtCurrencyCents(b.fees_total) : '—'}
                    </span>
                  </Td>
                  <Td right muted>{b ? fmtCurrencyCents(b.paid_total) : '—'}</Td>
                  <Td right bold style={{ color: (b?.outstanding_balance ?? 0) > 0 ? undefined : 'var(--color-text-muted)' }}>
                    {b && b.outstanding_balance > 0 ? fmtCurrencyCents(b.outstanding_balance) : '—'}
                  </Td>
                  <Td right style={{ color: (b?.overdue_balance ?? 0) > 0 ? 'var(--color-red-text)' : undefined, fontWeight: (b?.overdue_balance ?? 0) > 0 ? 500 : undefined }}>
                    {b && b.overdue_balance > 0 ? fmtCurrencyCents(b.overdue_balance) : '—'}
                  </Td>
                  <Td right muted>{b && b.writeoff_total > 0 ? fmtCurrencyCents(b.writeoff_total) : '—'}</Td>
                  <Td right muted>{b && b.credit_total > 0 ? fmtCurrencyCents(b.credit_total) : '—'}</Td>
                  {/* Why an outstanding balance closed without cash arriving.
                      Without it, a prepaid client reads as simply "not billed
                      much", with nothing on the row explaining the gap between
                      billed and paid. */}
                  <Td right muted>
                    <span title="Deposit credit drawn down against these invoices">
                      {b && b.deposit_applied_total > 0 ? fmtCurrencyCents(b.deposit_applied_total) : '—'}
                    </span>
                  </Td>
                  <Td right muted>{r.completed_count} / {r.deliverable_count}</Td>
                  <Td><Badge variant={r.status === 'Completed' ? 'green' : 'gray'}>{r.status}</Badge></Td>
                  <Td>{url ? <a href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: 'var(--color-blue)', fontSize: 11 }}>Open ↗</a> : '—'}</Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      {billingFor && (
        <InvoicesModal engagement={billingFor} onClose={() => setBillingFor(null)} />
      )}
      {contractFor && (
        <Modal
          onClose={() => setContractFor(null)}
          title={`Contract — ${contractFor.jira_issue_key} ${contractFor.summary}`}
          subtitle="What the client signed, and every change order against it"
          width={1040}
        >
          <EngagementContractPanel
            engagementId={contractFor.jira_issue_id}
            subject={contractFor.tempo_account_name ?? undefined}
          />
        </Modal>
      )}
    </>
  );
}

// ─── Billing cell + modal ───────────────────────────────────────────────────

/** Colour a ChargeOver raw-status string. Case-insensitive, conservative —
 *  anything unrecognised renders gray. Matches against substrings because
 *  ChargeOver tenants sometimes include qualifiers ("Partially Paid",
 *  "Write off / Bad debt"). */
export function statusBadgeVariant(raw: string | null | undefined): 'gray' | 'blue' | 'amber' | 'red' | 'green' {
  const s = (raw ?? '').toLowerCase();
  if (!s) return 'gray';
  // Order matters: check overdue and unapproved BEFORE paid, because the
  // hyphenated status_str can contain "paid" substrings in unrelated states.
  if (s.includes('draft') || s.includes('unapprov') || s.includes('pending') || s.includes('scheduled')) return 'gray';
  if (s.includes('void') || s.includes('cancel') || s.includes('delet')) return 'gray';
  if (s.includes('write') || s.includes('bad debt')) return 'gray';
  if (s.includes('overdue')) return 'red';
  if (s.includes('paid'))  return 'green';
  if (s.includes('unpaid') || s.includes('sent') || s.includes('issued') || s.includes('open')) return 'blue';
  return 'gray';
}

function InvoicesModal({ engagement, onClose }: { engagement: EngagementBudgetRow; onClose: () => void }) {
  const { data, isLoading, error } = useQuery(
    () => api.admin.engagementInvoices(engagement.jira_issue_id),
    [engagement.jira_issue_id],
  );
  const invoices: Invoice[] = data?.invoices ?? [];
  return (
    <Modal onClose={onClose} title={`Invoices — ${engagement.jira_issue_key} ${engagement.summary}`}>
      {isLoading ? <Spinner /> : error ? <EmptyState message={error} /> : invoices.length === 0 ? (
        <EmptyState message="No invoices linked to this engagement." />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Invoice #</Th>
              <Th>Status</Th>
              <Th>Issued</Th>
              <Th>Due</Th>
              <Th right>Total</Th>
              <Th right>Paid</Th>
              <Th right>Write-off</Th>
              <Th right>Credit</Th>
              <Th right>Balance</Th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((i) => {
              // Days past due is a purely date-based computation — only
              // meaningful for issued, unpaid invoices with a due_date.
              const statusLower = (i.raw_status ?? '').toLowerCase();
              const looksIssued = !statusLower.includes('draft') && !statusLower.includes('void')
                && !statusLower.includes('cancel') && i.balance_amount > 0.01;
              const daysPastDue = i.due_date && looksIssued
                ? Math.floor((Date.now() - new Date(i.due_date + 'T00:00:00Z').getTime()) / 86_400_000)
                : null;
              return (
                <Tr key={i.chargeover_invoice_id}>
                  <Td bold>
                    <InvoiceLink
                      invoiceId={i.chargeover_invoice_id}
                      display={i.invoice_number ?? i.chargeover_invoice_id}
                    />
                  </Td>
                  <Td>
                    <Badge variant={statusBadgeVariant(i.raw_status)}>
                      {i.raw_status || 'Unknown'}
                    </Badge>
                  </Td>
                  <Td muted style={{ fontSize: 11 }}>{i.date_issued ?? i.invoice_date ?? '—'}</Td>
                  <Td muted style={{ fontSize: 11 }}>
                    {i.due_date ?? '—'}
                    {daysPastDue != null && daysPastDue > 0 && (
                      <span style={{ marginLeft: 6, color: 'var(--color-red-text)', fontSize: 10 }}>
                        +{daysPastDue}d
                      </span>
                    )}
                  </Td>
                  <Td right>{fmtCurrencyCents(i.total_amount)}</Td>
                  <Td right muted>{fmtCurrencyCents(i.paid_amount)}</Td>
                  <Td right muted>{i.writeoff_amount > 0 ? fmtCurrencyCents(i.writeoff_amount) : '—'}</Td>
                  <Td right muted>{i.credit_amount > 0 ? fmtCurrencyCents(i.credit_amount) : '—'}</Td>
                  <Td right bold style={{ color: i.balance_amount > 0 && daysPastDue != null && daysPastDue > 0 ? 'var(--color-red-text)' : i.balance_amount > 0 ? undefined : 'var(--color-text-muted)' }}>
                    {fmtCurrencyCents(i.balance_amount)}
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Modal>
  );
}

